import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap } from 'lucide-react';

export const FinalCTASection = ({ onPartnerClick }) => {
  return (
    <section 
      id="final-cta" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="final-cta-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-medex-bg via-slate-900 to-medex-bg" />
        
        {/* Animated glow */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(6, 182, 212, 0.15) 0%, transparent 60%)',
          }}
          animate={{
            opacity: [0.5, 1, 0.5],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        
        {/* Neural network lines */}
        <div className="absolute inset-0 neural-bg opacity-30" />
      </div>
      
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
        >
          <Zap className="w-4 h-4 text-neon-cyan mr-2" />
          <span className="text-sm font-mono text-slate-300 uppercase tracking-wider">
            Join the Movement
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight"
        >
          This Isn't Transportation.
          <br />
          <span className="text-gradient">This Is Infrastructure.</span>
        </motion.h2>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          MedexOVA is more than a platform — it's a network, a movement, and the foundation 
          for the future of healthcare mobility. The question isn't whether to join. 
          It's whether you'll lead or follow.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-16"
        >
          <a
            href="#contact"
            className="btn-primary text-lg px-10 py-4 flex items-center group"
            data-testid="cta-join-network"
            onClick={(e) => { e.preventDefault(); onPartnerClick && onPartnerClick(); }}
          >
            Join the Network
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="https://ovadispatch.com/driver"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-lg px-10 py-4"
            data-testid="cta-become-driver-final"
          >
            Become a Driver
          </a>
          <a
            href="https://ovadispatch.com/client"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glow text-lg px-10 py-4"
            data-testid="cta-book-ride-final"
          >
            Book a Ride
          </a>
          <a
            href="#contact"
            className="btn-secondary text-lg px-10 py-4"
            data-testid="cta-partner-final"
            onClick={(e) => { e.preventDefault(); onPartnerClick && onPartnerClick(); }}
          >
            Partner With Us
          </a>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto"
        >
          {[
            { value: '847K+', label: 'Trips Completed' },
            { value: '12K+', label: 'Active Drivers' },
            { value: '500+', label: 'Healthcare Partners' },
            { value: '247', label: 'Cities Served' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl font-heading font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTASection;
